/**
 * Google Chat Integration Component
 * Admin-only settings card for configuring the app-wide Google Chat webhook.
 */

import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import { apiClient } from '@/lib/api'
import { Loader2, MessageSquare, CheckCircle, AlertCircle, Send } from 'lucide-react'
import { explainError } from '@/lib/explain-error'

interface GoogleChatStatus {
  configured: boolean
  is_enabled: boolean
  has_webhook_url: boolean
  webhook_url_masked: string
  enabled_events: string[]
  last_modified_date: string | null
  updated_by_name: string | null
}

const EVENT_OPTIONS = [
  { id: 'lead_created', label: 'New leads' },
  { id: 'opportunity_stage_changed', label: 'Opportunity stage changes' },
  { id: 'opportunity_closed_won', label: 'Deals closed (Closed Won)' },
  { id: 'task_assigned', label: 'Task assignments' },
  { id: 'task_due_reminder', label: 'Task due date reminders' },
] as const

export function GoogleChatIntegration() {
  const { toast } = useToast()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [status, setStatus] = useState<GoogleChatStatus | null>(null)
  const [webhookUrl, setWebhookUrl] = useState('')
  const [isEnabled, setIsEnabled] = useState(false)
  const [enabledEvents, setEnabledEvents] = useState<string[]>([])

  useEffect(() => {
    fetchStatus()
  }, [])

  const fetchStatus = async () => {
    try {
      setLoading(true)
      const data = await apiClient.getGoogleChatStatus()
      setStatus(data)
      setIsEnabled(data.is_enabled)
      setEnabledEvents(data.enabled_events || [])
    } catch (error) {
      console.error('Failed to fetch Google Chat status:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveWebhookUrl = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = webhookUrl.trim()
    if (!trimmed) {
      toast({
        title: 'URL required',
        description: 'Please enter a Google Chat webhook URL',
        variant: 'destructive',
      })
      return
    }

    try {
      setSaving(true)
      await apiClient.updateGoogleChatSettings({ webhook_url: trimmed })
      toast({ title: 'Saved', description: 'Webhook URL updated' })
      setWebhookUrl('')
      await fetchStatus()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to save webhook URL'),
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleToggleEnabled = async (enabled: boolean) => {
    try {
      setIsEnabled(enabled)
      await apiClient.updateGoogleChatSettings({ is_enabled: enabled })
      toast({
        title: enabled ? 'Enabled' : 'Disabled',
        description: `Google Chat notifications ${enabled ? 'enabled' : 'disabled'}`,
      })
      await fetchStatus()
    } catch (error: any) {
      setIsEnabled(!enabled) // Revert
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to update setting'),
        variant: 'destructive',
      })
    }
  }

  const handleToggleEvent = async (eventId: string, checked: boolean) => {
    const updated = checked
      ? [...enabledEvents, eventId]
      : enabledEvents.filter((e) => e !== eventId)

    try {
      setEnabledEvents(updated)
      await apiClient.updateGoogleChatSettings({ enabled_events: updated })
    } catch (error: any) {
      setEnabledEvents(enabledEvents) // Revert
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to update events'),
        variant: 'destructive',
      })
    }
  }

  const handleTest = async () => {
    try {
      setTesting(true)
      const result = await apiClient.testGoogleChat()
      if (result.success) {
        toast({ title: 'Test sent', description: 'Check your Google Chat space for the message' })
      } else {
        toast({ title: 'Test failed', description: result.message, variant: 'destructive' })
      }
    } catch (error: any) {
      toast({
        title: 'Test failed',
        description: error?.response?.data?.message || 'Failed to send test message',
        variant: 'destructive',
      })
    } finally {
      setTesting(false)
    }
  }

  const handleRemoveUrl = async () => {
    if (!window.confirm('Remove the webhook URL? This will stop all Google Chat notifications.'))
      return

    try {
      setSaving(true)
      await apiClient.updateGoogleChatSettings({ webhook_url: '', is_enabled: false })
      toast({ title: 'Removed', description: 'Webhook URL removed' })
      await fetchStatus()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to remove URL'),
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" aria-label="Loading Google Chat status" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          <CardTitle>Google Chat Integration</CardTitle>
        </div>
        <CardDescription>
          Send CRM event notifications to a Google Chat space via incoming webhook
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Connection Status */}
        <div
          className="flex items-center justify-between p-4 border rounded-lg"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-3">
            {status?.has_webhook_url ? (
              <>
                <CheckCircle className="h-5 w-5 text-[#2E7D5B]" aria-hidden="true" />
                <div>
                  <p className="font-medium">Configured</p>
                  <p className="text-sm text-muted-foreground">
                    Webhook: {status.webhook_url_masked}
                    {status.updated_by_name && (
                      <span className="ml-2 text-xs">(updated by {status.updated_by_name})</span>
                    )}
                  </p>
                </div>
              </>
            ) : (
              <>
                <AlertCircle className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                <div>
                  <p className="font-medium">Not Configured</p>
                  <p className="text-sm text-muted-foreground">
                    Enter a Google Chat incoming webhook URL to get started
                  </p>
                </div>
              </>
            )}
          </div>
          {status?.has_webhook_url && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleRemoveUrl}
              disabled={saving}
              aria-label="Remove webhook URL"
            >
              Remove
            </Button>
          )}
        </div>

        {/* Webhook URL Form */}
        <form onSubmit={handleSaveWebhookUrl} className="space-y-2">
          <Label htmlFor="google-chat-webhook-url">
            {status?.has_webhook_url ? 'Update Webhook URL' : 'Webhook URL'}
          </Label>
          <div className="flex gap-2">
            <Input
              id="google-chat-webhook-url"
              type="password"
              placeholder="https://chat.googleapis.com/v1/spaces/..."
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              disabled={saving}
              className="flex-1"
              aria-describedby="google-chat-webhook-help"
            />
            <Button
              type="submit"
              disabled={saving || !webhookUrl.trim()}
              aria-label="Save webhook URL"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : 'Save'}
            </Button>
          </div>
          <p id="google-chat-webhook-help" className="text-sm text-muted-foreground">
            In Google Chat, open your space &rarr; Manage webhooks &rarr; Create webhook &rarr; Copy
            URL
          </p>
        </form>

        {/* Settings (only when configured) */}
        {status?.has_webhook_url && (
          <>
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="google-chat-enabled">Enable notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Send event notifications to Google Chat
                </p>
              </div>
              <Switch
                id="google-chat-enabled"
                checked={isEnabled}
                onCheckedChange={handleToggleEnabled}
                aria-label="Toggle Google Chat notifications"
              />
            </div>

            {/* Event Selection */}
            <fieldset className="space-y-3">
              <legend className="text-sm font-medium">Notification events</legend>
              <p className="text-sm text-muted-foreground">
                Choose which CRM events trigger a Google Chat message
              </p>
              {EVENT_OPTIONS.map((event) => (
                <div key={event.id} className="flex items-center justify-between">
                  <Label
                    htmlFor={`event-${event.id}`}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {event.label}
                  </Label>
                  <Switch
                    id={`event-${event.id}`}
                    checked={enabledEvents.includes(event.id)}
                    onCheckedChange={(checked) => handleToggleEvent(event.id, checked)}
                    aria-label={`Enable ${event.label} notifications`}
                  />
                </div>
              ))}
            </fieldset>

            {/* Test Button */}
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={testing}
              aria-label="Send test message to Google Chat"
            >
              {testing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Send className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              {testing ? 'Sending...' : 'Send Test Message'}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  )
}
